import React, { useCallback, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { ArcStack, ArcText, ArcOutlinedSelect, ArcMenuItem } from '@src/core/ui';
import type { BarChartType } from '../../components/BarChart/types';
import { AnalyticsChartSection } from '../../components/AnalyticsChartSection';
import { SummaryStatCard } from '../../components/SummaryStatCard';
import { getChartIntervalApiParams } from '../../components/ChartTypeSwitcher';
import { useChartData } from '../../hooks';
import { useGetAnalyticsChartsQuery, useGetAnalyticsMetricsQuery } from '../../redux/api';
import { CHART_TYPE_TO_AGGREGATION } from '../../constants';
import { formatFlightTime, formatFlightDistance, formatDataUsage, formatCount } from '../../utils';
import { useUnitType } from '@src/core/features/settings';
import { useGetHubsQuery } from '@src/features/hubs/redux/api';

const ALL_HUBS = 'ALL';

const SUMMARY_LABELS = [
  'Active Drones',
  'Total Flight Count',
  'Total Flight Time',
  'Total Flight Distance',
  'Total Data Usage',
] as const;

export const AnalyticsPage: React.FC = () => {
  const [flightCountChartType, setFlightCountChartType] = useState<BarChartType>('daily');
  const [dataUsageChartType, setDataUsageChartType] = useState<BarChartType>('daily');
  const [flightCountPeriodOffset, setFlightCountPeriodOffset] = useState(0);
  const [dataUsagePeriodOffset, setDataUsagePeriodOffset] = useState(0);
  const [hubFilter, setHubFilter] = useState<string>(ALL_HUBS);

  const { data: hubs = [] } = useGetHubsQuery();
  const onHubFilterChange = (e: SelectChangeEvent<unknown>) => setHubFilter(String(e.target.value));
  const selectedHubId = hubFilter === ALL_HUBS ? null : Number(hubFilter);

  const onFlightCountChartTypeChange = useCallback((value: BarChartType) => {
    setFlightCountChartType(value);
    setFlightCountPeriodOffset(0);
  }, []);
  const onDataUsageChartTypeChange = useCallback((value: BarChartType) => {
    setDataUsageChartType(value);
    setDataUsagePeriodOffset(0);
  }, []);

  const { data: analyticsMetrics } = useGetAnalyticsMetricsQuery(
    selectedHubId != null ? { hubId: selectedHubId } : undefined,
  );
  // formatFlightDistance reads the unit system non-reactively; subscribe so the
  // summary re-renders (km <-> mi) when the user switches units.
  const unitType = useUnitType();

  const flightCountParams = useMemo(
    () => ({
      aggregation: CHART_TYPE_TO_AGGREGATION[flightCountChartType],
      ...getChartIntervalApiParams(flightCountChartType, flightCountPeriodOffset),
      ...(selectedHubId != null ? { hubId: selectedHubId } : {}),
    }),
    [flightCountChartType, flightCountPeriodOffset, selectedHubId],
  );
  const dataUsageParams = useMemo(
    () => ({
      aggregation: CHART_TYPE_TO_AGGREGATION[dataUsageChartType],
      ...getChartIntervalApiParams(dataUsageChartType, dataUsagePeriodOffset),
      ...(selectedHubId != null ? { hubId: selectedHubId } : {}),
    }),
    [dataUsageChartType, dataUsagePeriodOffset, selectedHubId],
  );

  const { data: flightCountCharts } = useGetAnalyticsChartsQuery(flightCountParams);
  const { data: dataUsageCharts } = useGetAnalyticsChartsQuery(dataUsageParams);

  const summaryStats = useMemo(() => {
    if (!analyticsMetrics) {
      return SUMMARY_LABELS.map((label) => ({ label, value: '—' as const }));
    }
    return [
      { label: SUMMARY_LABELS[0], value: formatCount(analyticsMetrics.activeDrones) },
      { label: SUMMARY_LABELS[1], value: formatCount(analyticsMetrics.totalFlightCount) },
      { label: SUMMARY_LABELS[2], value: formatFlightTime(analyticsMetrics.totalFlightTime) },
      { label: SUMMARY_LABELS[3], value: formatFlightDistance(analyticsMetrics.totalFlightDistance) },
      { label: SUMMARY_LABELS[4], value: formatDataUsage(analyticsMetrics.totalDataUsage) },
    ];
  }, [analyticsMetrics, unitType]);

  const flightCountData = useChartData(
    flightCountCharts?.flightCount,
    flightCountChartType,
    flightCountPeriodOffset
  );
  const dataUsageData = useChartData(
    dataUsageCharts?.dataUsage,
    dataUsageChartType,
    dataUsagePeriodOffset
  );

  return (
    <ArcStack px={5} pb={5} pt={3}>
      <ArcStack
        alignItems="flex-end"
        gap={2}
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
        <ArcStack spacing={0.5} sx={{ gridColumn: '1 / 5', minWidth: 0 }}>
          <ArcText.h2 fontWeight={500}>Drone Analytics</ArcText.h2>
          <ArcText.h5 color="text.secondary">Key metrics across your drone fleet</ArcText.h5>
        </ArcStack>
        <ArcOutlinedSelect
          label="HUB"
          fullWidth
          value={hubFilter}
          onChange={onHubFilterChange}>
          <ArcMenuItem value={ALL_HUBS}>All</ArcMenuItem>
          {hubs.map((hub) => (
            <ArcMenuItem key={hub.id} value={String(hub.id)}>
              {hub.name}
            </ArcMenuItem>
          ))}
        </ArcOutlinedSelect>
      </ArcStack>

      <ArcStack marginTop={3} direction="row" gap={2}>
        {summaryStats.map(({ label, value }) => (
          <SummaryStatCard key={label} label={label} value={value} />
        ))}
      </ArcStack>

      <ArcStack marginTop={3.5} spacing={3.5}>
        <AnalyticsChartSection
          title="Total Flight Count"
          chartType={flightCountChartType}
          periodOffset={flightCountPeriodOffset}
          onChartTypeChange={onFlightCountChartTypeChange}
          onPeriodOffsetChange={setFlightCountPeriodOffset}
          data={flightCountData}
        />
        <AnalyticsChartSection
          title="Total Data Usage"
          chartType={dataUsageChartType}
          periodOffset={dataUsagePeriodOffset}
          onChartTypeChange={onDataUsageChartTypeChange}
          onPeriodOffsetChange={setDataUsagePeriodOffset}
          data={dataUsageData}
          valueSuffix=" GB"
        />
      </ArcStack>
    </ArcStack>
  );
};
